import { get, type BookingList } from "../api.js";
import { dayKey } from "../format.js";
import { addDays, daysBetween, minutesOfDay, weekdayIndex } from "./grid.js";
import { diaryEntries, type DiaryEntry } from "./entries.js";

export interface BookingSuggestion {
  studentId: string;
  name: string;
}

export interface BookingSuggestionRange {
  from: string;
  to: string;
}

interface StudentHistory {
  studentId: string;
  name: string;
  count: number;
  latest: string;
}

/** The dates the Diary already has in hand are treated as covered. */
export function missingBookingSuggestionRange(
  slot: { startsAt: string },
  loaded: BookingSuggestionRange | null,
): BookingSuggestionRange | null {
  if (!Number.isFinite(Date.parse(slot.startsAt))) return null;
  const slotDay = dayKey(slot.startsAt);
  const from = addDays(slotDay, -28);
  const to = addDays(slotDay, -1);
  if (loaded === null || loaded.from > to || loaded.to < from) return { from, to };
  if (loaded.from <= from && loaded.to >= to) return null;

  const missingFrom = loaded.to < to ? addDays(loaded.to, 1) : from;
  const missingTo = loaded.from > from ? addDays(loaded.from, -1) : to;
  if (missingFrom > missingTo) return null;
  return { from: missingFrom, to: missingTo };
}

/**
 * Suggest students seen on a similar London weekday and wall-clock time in
 * the preceding four weeks. Diary entries provide hints only; BookOneLesson
 * confirms every suggested id through the scoped student search before use.
 */
export function suggestStudents(
  entries: readonly DiaryEntry[],
  slot: { startsAt: string; tutorId: string | null },
  now: Date = new Date(),
): BookingSuggestion[] {
  void now;
  if (!Number.isFinite(Date.parse(slot.startsAt))) return [];

  const slotDay = dayKey(slot.startsAt);
  const slotWeekday = weekdayIndex(slotDay);
  if (slotWeekday === null) return [];
  const slotMinute = minutesOfDay(slot.startsAt);
  const history = new Map<string, StudentHistory>();
  const seenBookings = new Set<string>();

  for (const entry of entries) {
    if (entry.anonymous || entry.studentId === null || entry.isSchools ||
      entry.studentName === null || entry.studentName.trim() === "") continue;
    if (slot.tutorId !== null && entry.tutorId !== slot.tutorId) continue;
    if (!Number.isFinite(Date.parse(entry.startsAt)) || entry.startsAt >= slot.startsAt) continue;

    const day = dayKey(entry.startsAt);
    const daysBefore = daysBetween(day, slotDay);
    if (daysBefore === null || daysBefore < 0 || daysBefore > 28) continue;
    if (weekdayIndex(day) !== slotWeekday) continue;
    if (Math.abs(minutesOfDay(entry.startsAt) - slotMinute) > 15) continue;

    if (seenBookings.has(entry.bookingId)) continue;
    seenBookings.add(entry.bookingId);

    const existing = history.get(entry.studentId);
    if (!existing) {
      history.set(entry.studentId, {
        studentId: entry.studentId,
        name: entry.studentName,
        count: 1,
        latest: entry.startsAt,
      });
    } else {
      existing.count += 1;
      if (entry.startsAt > existing.latest) {
        existing.latest = entry.startsAt;
        existing.name = entry.studentName;
      }
    }
  }

  return [...history.values()]
    .sort((a, b) => b.count - a.count || b.latest.localeCompare(a.latest) || a.studentId.localeCompare(b.studentId))
    .slice(0, 5)
    .map(({ studentId, name }) => ({ studentId, name }));
}

const bookingSuggestionCache = new Map<string, Promise<readonly DiaryEntry[] | null>>();

/** Cache both successful reads and failures for the browser session. */
export function readBookingSuggestionEntries(
  range: BookingSuggestionRange,
): Promise<readonly DiaryEntry[] | null> {
  const key = `${range.from}|${range.to}`;
  const cached = bookingSuggestionCache.get(key);
  if (cached) return cached;

  const request = get<BookingList>("/admin/bookings", { from: range.from, to: range.to })
    .then((bookings) => diaryEntries(bookings, { includeNoShows: true }))
    .catch(() => null);
  bookingSuggestionCache.set(key, request);
  return request;
}

/** Test isolation for the module-level session cache. */
export function clearBookingSuggestionCache(): void {
  bookingSuggestionCache.clear();
}
